/** Tappable pill button shown below learner single-desktop — opens the full-screen CommandBarModal. */
import { useState, useRef, useCallback } from 'react';
import { CyclingPlaceholder } from './CyclingPlaceholder';

interface CommandBarTriggerProps {
  placeholders: string[];
  onOpen: () => void;
  sizeScale?: number;
}

const STATIC_THRESHOLD = 160;

export function CommandBarTrigger({ placeholders, onOpen, sizeScale = 1 }: CommandBarTriggerProps) {
  const [narrow, setNarrow] = useState(false);
  const roRef = useRef<ResizeObserver | null>(null);
  const uiScale = Math.min(Math.max(sizeScale, 0.92), 1.35);
  const horizontalPadding = Math.round(14 * uiScale);
  const barHeight = Math.round(40 * uiScale);
  const textSize = +(14 * uiScale).toFixed(2);

  const measureRef = useCallback((node: HTMLButtonElement | null) => {
    roRef.current?.disconnect();
    if (!node) return;
    roRef.current = new ResizeObserver(([entry]) => {
      setNarrow(entry.contentBoxSize[0].inlineSize < STATIC_THRESHOLD);
    });
    roRef.current.observe(node);
  }, []);

  return (
    <button
      ref={measureRef}
      type="button"
      onClick={onOpen}
      className="flex w-full items-center overflow-hidden"
      style={{
        background: 'var(--shell-command-bg)',
        border: '1px solid var(--shell-command-border-strong)',
        borderRadius: 9999,
        padding: `0 ${horizontalPadding}px`,
        height: barHeight,
        backdropFilter: 'blur(12px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
        boxShadow: 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 1px 3px rgba(0,0,0,0.05)',
      }}
      aria-label="Open AI assistant"
    >
      {narrow ? (
        <span
          className="truncate"
          style={{
            fontSize: textSize,
            fontWeight: 450,
            color: 'var(--shell-placeholder)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          Ask a question...
        </span>
      ) : (
        <div className="relative min-w-0 flex-1">
          <CyclingPlaceholder items={placeholders} fontSize={textSize} />
          <span
            className="pointer-events-none invisible"
            style={{ fontSize: textSize, fontWeight: 450 }}
            aria-hidden
          >
            {'\u00A0'}
          </span>
        </div>
      )}
    </button>
  );
}
